import "@/i18n";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Pages
import { HomePage } from "@/pages/HomePage";
import { SearchPage } from "@/pages/SearchPage";
import { MaterialDetailPage } from "@/pages/MaterialDetailPage";
import { LoginPage, RegisterPage } from "@/pages/AuthPages";
import { WatchlistPage } from "@/pages/WatchlistPage";
import { RequestsPage } from "@/pages/RequestsPage";
import { GroupRequestsPage } from "@/pages/GroupRequestsPage";
import { DashboardPage } from "@/pages/DashboardPage";
import AdminPage from "@/pages/AdminPage";
import MasterDataPage from "@/pages/MasterDataPage";
import { ImportPage } from "@/pages/ImportPage";
import { MyMaterialsPage } from "@/pages/MyMaterialsPage";
import { MyGroupPage } from "@/pages/MyGroupPage";
import { ScenarioPage } from "@/pages/ScenarioPage";
import CreateEditMaterialPage from "@/pages/CreateEditMaterialPage";
import ExternalRegisterPage from "@/pages/ExternalRegisterPage";
import ExternalPortalPage from "@/pages/ExternalPortalPage";
import InvitationAcceptPage from "@/pages/InvitationAcceptPage";
import { LegalPage } from "@/pages/LegalPage";
import { TransactionsPage } from "@/pages/TransactionsPage";
import { MatchingDashboardPage } from "@/pages/MatchingDashboardPage";
import { AnalyticsPage } from "@/pages/AnalyticsPage";
import ForPartnersPage from "@/pages/ForPartnersPage";
import CommissionDashboardPage from "@/pages/CommissionDashboardPage";
import SellerPortalPage from "@/pages/SellerPortalPage";
import WantedMaterialsPage from "@/pages/WantedMaterialsPage";
import MyOffersPage from "@/pages/MyOffersPage";
import MessagesPage from "@/pages/MessagesPage";
import NotificationSettingsPage from "@/pages/NotificationSettingsPage";
import SubscriptionManagementPage from "@/pages/SubscriptionManagementPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import MarketplacePaymentSuccessPage from "@/pages/MarketplacePaymentSuccessPage";
import GroupAdminPortalPage from "@/pages/GroupAdminPortalPage";
import UserGuidePage from "@/pages/UserGuidePage";
import MyBugReportsPage from "@/pages/MyBugReportsPage";
import DevelopersPage from "@/pages/DevelopersPage";
import ListingsPage from "@/pages/ListingsPage";
import CreateEditListingPage from "@/pages/CreateEditListingPage";
import ListingDetailPage from "@/pages/ListingDetailPage";
import MyListingsPage from "@/pages/MyListingsPage";
import SavedSearchesPage from "@/pages/SavedSearchesPage";
import SeoLandingPage from "@/pages/SeoLandingPage";
import HubPage from "@/pages/HubPage";
import AdminInviteAcceptPage from "@/pages/AdminInviteAcceptPage";
import { BugReportButton } from "@/components/BugReportButton";

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false, requireManager = false }) => {
  const { user, loading, isAdmin, isAnyAdmin, isSiteManager } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
          <p className="text-muted-foreground text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: window.location.pathname }} replace />;
  }

  if (requireAdmin && !isAnyAdmin) {
    return <Navigate to="/" replace />;
  }

  if (requireManager && !isSiteManager && !isAnyAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  const { user } = useAuth();
  return (
    <>
      <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/search" element={<SearchPage />} />
      
      {/* Material Create/Edit - Protected (must be before :materialId) */}
      <Route 
        path="/material/new" 
        element={
          <ProtectedRoute>
            <CreateEditMaterialPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/material/:materialId/edit" 
        element={
          <ProtectedRoute>
            <CreateEditMaterialPage />
          </ProtectedRoute>
        } 
      />
      <Route path="/material/:materialId" element={<MaterialDetailPage />} />

      {/* Listings (Spare Parts + Machines) */}
      <Route path="/listings" element={<ListingsPage />} />
      {/* SEO Landing Pages for new verticals */}
      <Route path="/ersatzteile" element={<SeoLandingPage vertical="spare_parts" />} />
      <Route path="/druckmaschinen" element={<SeoLandingPage vertical="machines" />} />
      <Route
        path="/listing/new"
        element={<ProtectedRoute><CreateEditListingPage /></ProtectedRoute>}
      />
      <Route
        path="/listing/:listingId/edit"
        element={<ProtectedRoute><CreateEditListingPage /></ProtectedRoute>}
      />
      <Route path="/listing/:listingId" element={<ListingDetailPage />} />
      <Route
        path="/my-listings"
        element={<ProtectedRoute><MyListingsPage /></ProtectedRoute>}
      />
      <Route
        path="/saved-searches"
        element={<ProtectedRoute><SavedSearchesPage /></ProtectedRoute>}
      />

      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/register/external" element={<ExternalRegisterPage />} />
      <Route path="/invitations/:invitationId/register" element={<InvitationAcceptPage />} />
      <Route path="/legal/:slug" element={<LegalPage />} />
      <Route path="/admin-invite/:token" element={<AdminInviteAcceptPage />} />
      <Route path="/hub" element={<ProtectedRoute><HubPage /></ProtectedRoute>} />
      
      {/* Protected Routes - User */}
      <Route 
        path="/my-materials" 
        element={
          <ProtectedRoute>
            <MyMaterialsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/watchlist" 
        element={
          <ProtectedRoute>
            <WatchlistPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/requests" 
        element={
          <ProtectedRoute>
            <RequestsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/group-requests" 
        element={
          <ProtectedRoute>
            <GroupRequestsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/standard-materials" 
        element={<Navigate to="/matching?tab=standard" replace />}
      />
      <Route 
        path="/transactions" 
        element={
          <ProtectedRoute>
            <TransactionsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/matching" 
        element={
          <ProtectedRoute>
            <MatchingDashboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/analytics" 
        element={
          <ProtectedRoute>
            <AnalyticsPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Protected Routes - Manager */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/my-group" 
        element={
          <ProtectedRoute>
            <MyGroupPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/scenario" 
        element={
          <ProtectedRoute requireAdmin>
            <ScenarioPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/import" 
        element={
          <ProtectedRoute requireManager>
            <ImportPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Protected Routes - Admin */}
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute requireAdmin>
            <AdminPage />
          </ProtectedRoute>
        } 
      />

      {/* Stammdaten (master data) — admins + group admins + site managers */}
      <Route
        path="/master-data"
        element={
          <ProtectedRoute requireManager>
            <MasterDataPage />
          </ProtectedRoute>
        }
      />
      {/* External Company Portal */}
      <Route 
        path="/my-company" 
        element={
          <ProtectedRoute>
            <ExternalPortalPage />
          </ProtectedRoute>
        } 
      />

      {/* Partner Landing Page */}
      <Route path="/for-partners" element={<ForPartnersPage />} />
      <Route path="/developers" element={<DevelopersPage />} />

      {/* Commission Dashboard - Admin only */}
      <Route
        path="/commission"
        element={
          <ProtectedRoute requireAdmin>
            <CommissionDashboardPage />
          </ProtectedRoute>
        }
      />

      {/* Seller Portal - any logged-in user who is_seller */}
      <Route
        path="/seller-portal"
        element={
          <ProtectedRoute>
            <SellerPortalPage />
          </ProtectedRoute>
        }
      />

      {/* Wanted Materials - any logged-in user can browse; premium sellers can post */}
      <Route
        path="/wanted"
        element={
          <ProtectedRoute>
            <WantedMaterialsPage />
          </ProtectedRoute>
        }
      />

      {/* My Offers (Seller view of submitted offers) */}
      <Route
        path="/my-offers"
        element={
          <ProtectedRoute>
            <MyOffersPage />
          </ProtectedRoute>
        }
      />

      {/* Messages */}
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        }
      />

      {/* Notification Settings */}
      <Route
        path="/notification-settings"
        element={
          <ProtectedRoute>
            <NotificationSettingsPage />
          </ProtectedRoute>
        }
      />

      {/* Subscription Management - Admin only */}
      <Route
        path="/subscriptions"
        element={
          <ProtectedRoute requireAdmin>
            <SubscriptionManagementPage />
          </ProtectedRoute>
        }
      />

      {/* Payment Success */}
      <Route
        path="/payment-success"
        element={
          <ProtectedRoute>
            <PaymentSuccessPage />
          </ProtectedRoute>
        }
      />

      {/* Marketplace Order Payment Success */}
      <Route
        path="/marketplace-payment-success"
        element={
          <ProtectedRoute>
            <MarketplacePaymentSuccessPage />
          </ProtectedRoute>
        }
      />

      {/* Group Admin Portal */}
      <Route
        path="/group-admin"
        element={
          <ProtectedRoute>
            <GroupAdminPortalPage />
          </ProtectedRoute>
        }
      />

      {/* User Guide & FAQ */}
      <Route
        path="/guide"
        element={
          <ProtectedRoute>
            <UserGuidePage />
          </ProtectedRoute>
        }
      />

      {/* My Bug Reports */}
      <Route
        path="/my-reports"
        element={
          <ProtectedRoute>
            <MyBugReportsPage />
          </ProtectedRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    <BugReportButton user={user} />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
